import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, ImagePlus } from "lucide-react";
import { API, authHeaders, imageSrc, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";

const EMPTY = { name: "", description: "", price: "", tag: "", image: "" };

export default function MenuTab() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    axios.get(`${API}/menu`).then((res) => setItems(res.data)).catch(() => toast.error("Could not load menu"));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ name: item.name, description: item.description, price: item.price, tag: item.tag, image: item.image });
    setDialogOpen(true);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await axios.post(`${API}/upload`, data, authHeaders());
      setForm((f) => ({ ...f, image: res.data.path }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/menu/${editing.id}`, form, authHeaders());
        toast.success("Item updated");
      } else {
        await axios.post(`${API}/menu`, form, authHeaders());
        toast.success("Item added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}" from the menu?`)) return;
    try {
      await axios.delete(`${API}/menu/${item.id}`, authHeaders());
      toast.success("Item deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-menu-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-zinc-400">{items.length} item{items.length === 1 ? "" : "s"} on the menu</p>
        <button
          data-testid="menu-add-button"
          onClick={openNew}
          className="inline-flex items-center gap-2 rounded-full bg-disco-yellow text-disco-void text-xs font-bold px-5 py-2.5 transition-all duration-300 hover:shadow-[0_0_20px_rgba(226,255,0,0.35)] active:scale-95"
        >
          <Plus size={14} /> Add Item
        </button>
      </div>

      <div className="border border-white/10 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-zinc-400">Photo</TableHead>
              <TableHead className="text-zinc-400">Name</TableHead>
              <TableHead className="text-zinc-400">Tag</TableHead>
              <TableHead className="text-zinc-400">Price</TableHead>
              <TableHead className="text-zinc-400 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} data-testid={`admin-menu-row-${item.id}`} className="border-white/5">
                <TableCell>
                  {item.image ? (
                    <img src={imageSrc(item.image)} alt={item.name} className="w-14 h-14 object-cover border border-white/10" />
                  ) : (
                    <span className="text-zinc-600 text-xs">No photo</span>
                  )}
                </TableCell>
                <TableCell className="text-zinc-100 font-medium">{item.name}</TableCell>
                <TableCell className="text-disco-lavender text-xs uppercase tracking-wider">{item.tag}</TableCell>
                <TableCell className="text-disco-yellow font-semibold">{item.price}</TableCell>
                <TableCell className="text-right">
                  <button
                    data-testid={`menu-edit-${item.id}`}
                    onClick={() => openEdit(item)}
                    className="p-2 text-zinc-400 hover:text-disco-teal transition-colors"
                    aria-label={`Edit ${item.name}`}
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    data-testid={`menu-delete-${item.id}`}
                    onClick={() => remove(item)}
                    className="p-2 text-zinc-400 hover:text-red-400 transition-colors"
                    aria-label={`Delete ${item.name}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow className="border-white/5">
                <TableCell colSpan={5} className="text-center text-zinc-500 py-10 text-sm">
                  No menu items yet. Add your first dog.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="menu-item-dialog" className="bg-disco-surface border-white/10 text-zinc-100 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide text-2xl">
              {editing ? "Edit Item" : "New Menu Item"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300 text-xs uppercase tracking-widest">Name</Label>
              <Input
                data-testid="menu-form-name"
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="bg-white/5 border-white/10 text-zinc-100"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300 text-xs uppercase tracking-widest">Description</Label>
              <Textarea
                data-testid="menu-form-description"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="bg-white/5 border-white/10 text-zinc-100 resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300 text-xs uppercase tracking-widest">Price</Label>
                <Input
                  data-testid="menu-form-price"
                  value={form.price}
                  onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                  placeholder="$9"
                  className="bg-white/5 border-white/10 text-zinc-100"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300 text-xs uppercase tracking-widest">Tag</Label>
                <Input
                  data-testid="menu-form-tag"
                  value={form.tag}
                  onChange={(e) => setForm((f) => ({ ...f, tag: e.target.value }))}
                  placeholder="Fan Favorite"
                  className="bg-white/5 border-white/10 text-zinc-100"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300 text-xs uppercase tracking-widest">Photo</Label>
              <div className="flex items-center gap-4">
                {form.image && (
                  <img src={imageSrc(form.image)} alt="Menu item preview" className="w-16 h-16 object-cover border border-white/10" />
                )}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="menu-image-upload-input" />
                <button
                  type="button"
                  data-testid="menu-image-upload-button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 rounded-full border border-disco-teal/50 text-disco-teal text-xs font-semibold px-4 py-2 transition-colors duration-300 hover:bg-disco-teal/10 active:scale-95 disabled:opacity-50"
                >
                  <ImagePlus size={14} />
                  {uploading ? "Uploading..." : form.image ? "Change Photo" : "Upload Photo"}
                </button>
              </div>
            </div>
            <button
              data-testid="menu-save-button"
              type="submit"
              disabled={saving}
              className="w-full rounded-full bg-disco-yellow text-disco-void font-semibold py-3 text-sm transition-all duration-300 hover:shadow-[0_0_20px_rgba(226,255,0,0.35)] active:scale-[0.98] disabled:opacity-50"
            >
              {saving ? "Saving..." : editing ? "Save Changes" : "Add to Menu"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
